<template>
  <section class="section">
    <box class="copyright-box">
      <div slot="header" class="copyright-header">
        {{ $t('copyright.sectionTitle') }}
      </div>

      <div class="copyright-content">

        <div class="info-grid">
          <!-- Company -->
          <div class="info-row">
            <div class="info-label">{{ $t('copyright.company') }}:</div>
            <div class="info-value">
              {{ licenseData.company }}
              <button
                v-if="licenseData.company"
                class="button is-text is-small edit-btn"
                @click="editCompany"
              >
                <octicon :icon="pencil" />
              </button>
            </div>
          </div>

          <!-- Copyright Owner -->
          <div class="info-row">
            <div class="info-label">{{ $t('copyright.copyrightOwner') }}:</div>
            <div class="info-value">{{ licenseData.copyrightOwner }}</div>
          </div>

          <!-- Product -->
          <div class="info-row">
            <div class="info-label">{{ $t('copyright.product') }}:</div>
            <div class="info-value">{{ licenseData.product }}</div>
          </div>

          <!-- License Code -->
          <div class="info-row">
            <div class="info-label">{{ $t('copyright.licenseCode') }}:</div>
            <div class="info-value license-code">{{ licenseData.licenseCode }}</div>
          </div>

          <!-- Activation Date -->
          <div class="info-row">
            <div class="info-label">{{ $t('copyright.activationDate') }}:</div>
            <div class="info-value">{{ licenseData.activationDate }}</div>
          </div>

          <!-- Expiration Date -->
          <div class="info-row">
            <div class="info-label">{{ $t('copyright.expirationDate') }}:</div>
            <div class="info-value">{{ licenseData.expirationDate }}</div>
          </div>

          <!-- Software Version -->
          <div class="info-row">
            <div class="info-label">{{ $t('copyright.softwareVersion') }}:</div>
            <div class="info-value">{{ licenseData.softwareVersion }}</div>
          </div>
        </div>

        <!-- Update License Section -->
        <div class="update-section">
          <h3 class="section-title">{{ $t('copyright.updateLicense') }}</h3>
          <p class="contact-info">
            {{ contactInfoPrefix }}
            <a :href="`tel:${phone}`" class="contact-link">{{ phone }}</a>
            {{ contactInfoMiddle }}
            <a :href="`mailto:${email}`" class="contact-link">{{ email }}</a>{{ contactInfoSuffix }}
          </p>
        </div>
      </div>
    </box>
  </section>
</template>

<script>
import { pencil } from 'octicons-vue'

export default {
  name: 'Copyright',
  data() {
    return {
      licenseData: {
        company: '',
        copyrightOwner: '',
        product: '',
        licenseCode: '',
        activationDate: '',
        expirationDate: '',
        softwareVersion: ''
      },
      loading: false
    }
  },
  computed: {
    pencil() {
      return pencil
    },
    phone() {
      return this.licenseData.contactPhone || '(+84) 24 7300 6066'
    },
    email() {
      return this.licenseData.contactEmail || 'contact@cyradar.com'
    },
    contactInfoPrefix() {
      const fullText = this.$t('copyright.contactInfo', { phone: '{phone}', email: '{email}' })
      return fullText.split('{phone}')[0]
    },
    contactInfoMiddle() {
      const fullText = this.$t('copyright.contactInfo', { phone: '{phone}', email: '{email}' })
      const parts = fullText.split('{phone}')
      if (parts[1]) {
        return parts[1].split('{email}')[0]
      }
      return ''
    },
    contactInfoSuffix() {
      const fullText = this.$t('copyright.contactInfo', { phone: '{phone}', email: '{email}' })
      const parts = fullText.split('{email}')
      if (parts[1]) {
        return parts[1]
      }
      return ''
    },
    isDark() {
      return this.$store.state.ui.dark
    }
  },
  mounted() {
    this.fetchLicenseData()
  },
  methods: {
    editCompany() {
      console.log('Edit company')
    },
    async fetchLicenseData() {
      this.loading = true
      try {
        const response = await this.$http.get('/api/v1/copyright')
        if (response && response.data && response.data.data) {
          const data = response.data.data
          this.licenseData = {
            company: data.company || '',
            copyrightOwner: data.copyrightOwner || '',
            product: data.product || '',
            licenseCode: data.licenseCode || '',
            activationDate: data.activationDate || '',
            expirationDate: data.expirationDate || '',
            softwareVersion: data.softwareVersion || '',
            contactPhone: data.contactPhone || '',
            contactEmail: data.contactEmail || ''
          }
        }
      } catch (error) {
        console.error('Failed to fetch copyright data:', error)
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: this.$t('copyright.fetchError') || 'Failed to load copyright information'
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$x: #1f2128;
$x-light: lighten($x, 5%);
$y: #dddddf;
$y-dark: darken($y, 5%);

.section {
  padding: 1rem 1.5rem 1.5rem 1.5rem;
}

.title {
  margin-bottom: 1.5rem;
  
  .level {
    margin-top: 0.5rem;
  }
  
  .subtitle {
    font-size: 0.875rem;
    color: #7a7a7a;
    font-weight: normal;
  }
}

.copyright-box {
  max-width: 1200px;
}

.copyright-header {
  font-size: 1.125rem;
  font-weight: 600;
}

.copyright-content {
  padding: 1.5rem;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #363636;
  
  .dashboard.is-dark & {
    color: $y;
  }
}

.info-grid {
  display: grid;
  gap: 0.75rem;
  margin-bottom: 2rem;
}

.info-row {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 1rem;
  padding: 0.75rem;
  border-bottom: 1px solid #e8e8e8;
  
  .dashboard.is-dark & {
    border-bottom-color: rgba($y, 0.1);
  }
  
  &:last-child {
    border-bottom: none;
  }
}

.info-label {
  font-weight: 600;
  color: #4a4a4a;
  
  .dashboard.is-dark & {
    color: darken($y, 10%);
  }
}

.info-value {
  color: #363636;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  .dashboard.is-dark & {
    color: $y;
  }
  
  &.license-code {
    font-family: 'Courier New', monospace;
    font-weight: 600;
    letter-spacing: 0.5px;
  }
  
  &.days-remaining {
    color: #48c774;
    font-weight: 600;
    
    .dashboard.is-dark & {
      color: #48c774;
    }
  }
}

.edit-btn {
  padding: 0.25rem 0.5rem;
  height: auto;
  min-height: auto;
  color: #3273dc;
  
  .dashboard.is-dark & {
    color: #3d8eb9;
  }
  
  &:hover {
    background-color: rgba(50, 115, 220, 0.1);
    
    .dashboard.is-dark & {
      background-color: rgba(61, 142, 185, 0.1);
    }
  }
}

.update-section {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid #e8e8e8;
  
  .dashboard.is-dark & {
    border-top-color: rgba($y, 0.2);
  }
}

.contact-info {
  color: #4a4a4a;
  line-height: 1.6;

  .dashboard.is-dark & {
    color: darken($y, 10%);
  }
}

.contact-link {
  color: #3273dc;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;

  .dashboard.is-dark & {
    color: #3d8eb9;
  }

  &:hover {
    text-decoration: underline;
    color: darken(#3273dc, 10%);

    .dashboard.is-dark & {
      color: lighten(#3d8eb9, 10%);
    }
  }
}

// Responsive design
@media screen and (max-width: 768px) {
  .info-row {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  .info-label {
    font-size: 0.875rem;
  }
}
</style>

